import {
  appendCaseDetailRefreshParam,
  orchestrateTwoPhaseCaseDetailFetch,
  runCaseDetailDiff,
  appServiceLines,
  type CaseDetailCacheOutcome,
  type TwoPhaseCaseDetailResult
} from '@codametrix/ui-common';
import { isCaseDetailTwoPhaseEnabled } from '@codametrix/client-runtime';
import { api, apiWithCacheMeta } from '../core/net';

export type CaseDetailTwoPhaseFetchResult = TwoPhaseCaseDetailResult<CMxAPI.Case>;

export type FetchCaseDetailTwoPhaseOptions = {
  serviceLine?: string;
  /**
   * CaseRun the screen is displaying. Omitted on the Cases list, which renders the primary run —
   * the comparer's default when no process id is supplied.
   */
  processId?: string | number;
  onPhase1Complete?: (
    caseDetail: CMxAPI.Case,
    xCache: CaseDetailCacheOutcome | undefined
  ) => void | Promise<void>;
};

export async function fetchCaseDetailTwoPhase(
  endpoint: string,
  detailSignal: AbortSignal,
  reconcileSignal: AbortSignal,
  options?: FetchCaseDetailTwoPhaseOptions
): Promise<CaseDetailTwoPhaseFetchResult> {
  if (!isCaseDetailTwoPhaseEnabled()) {
    const caseDetail = await api<CMxAPI.Case>({
      endpoint,
      init: { signal: detailSignal }
    });
    return { data: caseDetail, deferSupportingData: false };
  }

  const phase2Endpoint =
    options?.serviceLine && appServiceLines.isIPC(options.serviceLine)
      ? appendCaseDetailRefreshParam(endpoint)
      : endpoint;

  const request =
    (
      url: string,
      cacheOptions: { acceptCache?: boolean; cacheReplace?: boolean },
      signal: AbortSignal
    ) =>
    () =>
      apiWithCacheMeta<CMxAPI.Case>(
        { endpoint: url, init: { signal } },
        cacheOptions
      );

  return orchestrateTwoPhaseCaseDetailFetch({
    phase1: request(endpoint, { acceptCache: true }, detailSignal),
    phase2: request(
      phase2Endpoint,
      { acceptCache: false, cacheReplace: true },
      reconcileSignal
    ),
    fallback: request(endpoint, { acceptCache: false }, detailSignal),
    onPhase1Complete: (data, xCache) =>
      options?.onPhase1Complete?.(data, xCache),
    differs: (phase1, phase2) =>
      runCaseDetailDiff(
        phase1,
        phase2,
        {
          processId: options?.processId,
          phase2UsedRefresh: phase2Endpoint !== endpoint
        },
        reconcileSignal
      )
  });
}
